import { Fragment, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { Hit } from 'instantsearch.js';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import AddToCart from '@/components/customercenter-ui/svgs/addToCart';
import NoImage from '@/components/customercenter-ui/svgs/noImage';
import { renderProductUrl } from '@/components/customercenter-ui/utils/formatUtils';
import StarFill from '@/customercenter-ui/icons/startFill';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { filterOnSaleItem } from '@/helpers/utils/AlgoliaInsights';
import { clearanceTextClass } from '@/helpers/utils/brandButton';
import { formatAmount, getSelectedSite, TranslateText } from '@/helpers/utils/siteUtils';
import { DEFAULT_SITE_LANG } from '../../../../../constants/constant';
import ContentStackImage from '../../../common/contentStackImage/contentStackImage';
import { SwatchThumbnail } from '../../product/search/components/swatch';
import { PageTexts } from '../../product/search/i18n';
import { AlgoliaProductTypes, ProductDetailsSectionProps, Swatch } from '../../product/search/types';
import { Button } from '../../ui/button';

type HitProps = {
  hit: Hit<AlgoliaProductTypes>;
  crossSell: boolean;
};

const getUpdatedProductUrl = (productUrl: string): string => {
  if (!productUrl) return productUrl;
  if (productUrl.includes('crossSell=true')) {
    return productUrl;
  }
  if (productUrl.includes('?')) {
    return `${productUrl}&crossSell=true`;
  }
  return `${productUrl}?crossSell=true`;
};

export const ProductItem = ({ hit }: HitProps) => {
  const [selectedSwatch, setSelectedSwatch] = useState<Swatch | null>(null);
  const [productUrl, setProductURL] = useState(
    renderProductUrl(`/product/${hit?.pdpSeo}/${hit?.key}${`?crossSell=true`}`),
  );

  const handleSwatchSelect = (swatch: Swatch) => {
    setSelectedSwatch(swatch);
    setProductURL(`/product/${swatch?.pdpSeo}/${swatch?.key}${`?crossSell=true`}`);
    updatePrices(swatch);
  };

  const handleMasterProductSelect = () => {
    setSelectedSwatch(null);
    setProductURL(renderProductUrl(`/product/${hit?.pdpSeo}/${hit?.key}${`?crossSell=true`}`));
    resetPrices();
  };

  const updatePrices = (swatch: Swatch | null) => {
    if (!hit.defaultWasPrice || !hit.defaultPricePerMonth) {
      hit.defaultWasPrice = hit.wasPrice;
      hit.defaultPricePerMonth = hit.pricePerMonth;
    }

    if (swatch) {
      hit.wasPrice = swatch.wasPrice;
      hit.pricePerMonth = swatch.pricePerMonth;
    }
  };

  const resetPrices = () => {
    hit.wasPrice = hit.defaultWasPrice as string | undefined;
    hit.pricePerMonth = hit.defaultPricePerMonth as string | undefined;
  };

  return (
    <main className="mb-6 flex flex-col gap-4 px-4 py-6">
      <div>
        {' '}
        <ProductImageSection hit={hit} selectedSwatch={selectedSwatch} productUrl={productUrl} />{' '}
        <section className="order-2 lg:order-1">
          <SwatchesSection
            hit={hit}
            selectedSwatch={selectedSwatch}
            onSwatchSelect={handleSwatchSelect}
            onMasterProductSelect={handleMasterProductSelect}
          />
        </section>
      </div>
      <ProductDetailsSection
        hit={hit}
        selectedSwatch={selectedSwatch}
        onSwatchSelect={handleSwatchSelect}
        onMasterProductSelect={handleMasterProductSelect}
        productUrl={productUrl}
      />
    </main>
  );
};
export const ProductImageSection = ({
  hit,
  selectedSwatch,
  productUrl,
}: {
  hit: HitProps['hit'];
  selectedSwatch: Swatch | null;
  productUrl: string;
}) => {
  const siteId = getSelectedSite() ?? 'ZBSite';

  return (
    <section className="flex justify-center">
      <section className="relative h-[220px] w-[220px]">
        {renderProductImage(hit, selectedSwatch, true, productUrl)}
        {hit?.discount && (
          <section className="absolute left-1 top-1">
            <section
              className={`min-h-[30px] rounded-none ${clearanceTextClass(
                siteId,
              )} px-3 py-1 text-center text-sm font-bold leading-7 text-white`}
            >
              {hit?.discount}
            </section>
          </section>
        )}
      </section>
    </section>
  );
};

export const ProductDetailsSection = ({ hit, selectedSwatch, show, productUrl }: ProductDetailsSectionProps) => {
  return (
    <section className="md:my-3">
      <section className={show ? '' : 'flex flex-col'}>
        <section className="order-1 lg:order-2">
          <Link href={getUpdatedProductUrl(productUrl)}>
            <p className="min-h-10 mt-3 line-clamp-2 h-10 break-words text-[15px] font-medium leading-[21px] text-black">
              {hit?.displayName}
            </p>
          </Link>
        </section>
      </section>
      <PriceSection hit={hit} selectedSwatch={selectedSwatch} productUrl={productUrl} />
    </section>
  );
};

const PriceSection = ({
  hit,
  productUrl,
}: {
  hit: HitProps['hit'];
  selectedSwatch: Swatch | null;
  productUrl: string;
}) => {
  const router = useRouter();
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const projectSettings: ProjectSettings = useProjSettings();
  const siteId = getSelectedSite() ?? 'ZBSite';

  return (
    <div className="min-h-[50px]">
      <div className="mt-3 min-h-[50px]">
        {hit?.minListPrice ? (
          <p className="break-words text-[16px] font-normal leading-[19.6px] text-[#666666]">
            {(filterOnSaleItem(hit, projectSettings) || hit?.onDiscount || hit?.onNewListPrice) && hit?.minSalePrice ? (
              <>
                <strong
                  className={`break-words pr-2 text-base font-bold leading-[20.8px] ${clearanceTextClass(siteId)}`}
                >
                  {hit?.maxSalePrice && hit?.minSalePrice && hit?.maxSalePrice > hit?.minSalePrice ? (
                    <>
                      {formatAmount(hit?.minSalePrice.toString())} - {formatAmount(hit?.maxSalePrice.toString())}
                    </>
                  ) : (
                    <>{formatAmount(hit?.minSalePrice.toString())}</>
                  )}
                </strong>
                <em className="break-words not-italic line-through">
                  {hit?.maxListPrice && hit?.maxListPrice > hit?.minListPrice ? (
                    <>
                      {formatAmount(hit?.minListPrice.toString())} - {formatAmount(hit?.maxListPrice.toString())}
                    </>
                  ) : (
                    <strong
                      className={`break-words pr-2 text-lg font-bold leading-[23.4px] ${clearanceTextClass(siteId)}`}
                    >
                      {formatAmount(hit?.minListPrice.toString())}
                    </strong>
                  )}
                </em>
              </>
            ) : (
              <span>
                {hit?.maxListPrice && hit?.maxListPrice > hit?.minListPrice ? (
                  <>
                    {formatAmount(hit?.minListPrice.toString())} - {formatAmount(hit?.maxListPrice.toString())}
                  </>
                ) : (
                  <strong
                    className={`break-words pr-2 text-lg font-bold leading-[23.4px] ${clearanceTextClass(siteId)}`}
                  >
                    {formatAmount(hit?.minListPrice.toString())}
                  </strong>
                )}
              </span>
            )}
          </p>
        ) : (
          <></>
        )}

        {hit?.pricePerMonth && (
          <section>
            <strong className="inline-block text-lg font-bold leading-[23.4px] text-black">
              {formatAmount(hit?.pricePerMonth)}/month
            </strong>
          </section>
        )}
      </div>
      <section className="flex h-4 items-center">
        {hit?.ratingCount && (
          <section className="flex items-center gap-1">
            <StarFill value={Number(hit?.averageRating)} count={5} productId={hit?.objectID} /> ({hit?.ratingCount ?? 0}
            )
          </section>
        )}
      </section>

      <Button
        className="mt-4 flex h-11 w-[150px] items-center gap-2 rounded-md border border-[#B6B6B8] px-4 shadow-sm"
        type="button"
        onClick={() => router.push(getUpdatedProductUrl(productUrl))}
      >
        <AddToCart className="h-4 w-4" />
        {Texts.addToCart}
      </Button>
    </div>
  );
};

const SwatchesSection = ({
  hit,
  selectedSwatch,
  onSwatchSelect,
  onMasterProductSelect,
}: {
  hit: HitProps['hit'];
  selectedSwatch: Swatch | null;
  onSwatchSelect: (swatch: Swatch) => void;
  onMasterProductSelect: () => void;
}) => {
  return (
    <div className="relative mt-2 flex w-max gap-2">
      <MasterProductSwatch hit={hit} isSelected={!selectedSwatch} onMasterProductSelect={onMasterProductSelect} />
      {hit?.swatches &&
        hit.swatches
          .slice(0, 3)
          .map((swatch) => (
            <SwatchThumbnail
              key={swatch.id}
              swatch={swatch}
              isSelected={selectedSwatch === swatch}
              onSwatchSelect={onSwatchSelect}
            />
          ))}
      {hit?.swatches && hit.swatches.length > 4 && (
        <div className="flex items-center">
          <Listbox>
            <div className="">
              <ListboxButton className="relative w-full cursor-pointer bg-white  pr-5  text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                <span className="block truncate text-sm leading-[19.6px] text-black">+{hit.swatches.length - 4}</span>
              </ListboxButton>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions className="absolute right-0 top-10 z-10 mt-1 w-[163px]  rounded-md  bg-white text-base ring-1 ring-black/5 focus:outline-none sm:text-sm md:right-0 lg:-right-4">
                  <section className="relative rounded-md border border-foggyGray  bg-white py-3 shadow-md">
                    <div className="absolute right-4 top-0 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 border-l border-t border-foggyGray bg-white"></div>
                    <ul className="customtablescrollbar grid max-h-[277px] gap-3 overflow-y-auto px-2">
                      <MasterProductSwatch
                        hit={hit}
                        isSelected={!selectedSwatch}
                        onMasterProductSelect={onMasterProductSelect}
                      />
                      {hit?.swatches &&
                        hit.swatches.map((swatch, index) => (
                          <li className="flex items-center gap-2" key={`swatch_${index}`}>
                            <SwatchThumbnail
                              key={swatch.id}
                              swatch={swatch}
                              isSelected={selectedSwatch === swatch}
                              onSwatchSelect={onSwatchSelect}
                            />
                            <span>{swatch.color}</span>
                          </li>
                        ))}
                    </ul>
                  </section>
                </ListboxOptions>
              </Transition>
            </div>
          </Listbox>
        </div>
      )}
    </div>
  );
};

const MasterProductSwatch = ({
  hit,
  isSelected,
  onMasterProductSelect,
}: {
  hit: HitProps['hit'];
  isSelected: boolean;
  onMasterProductSelect: () => void;
}) => {
  return (
    <div
      aria-hidden="true"
      className={`h-9 w-9 cursor-pointer overflow-hidden rounded-full border ${
        isSelected ? 'border-2 border-black' : 'border-foggyGray'
      }`}
      style={{ backgroundColor: 'transparent' }}
      onClick={onMasterProductSelect}
    >
      {hit?.mainImageURL ? (
        <ContentStackImage src={hit.mainImageURL} imageHeight={32} imageWidth={32} alt="" />
      ) : (
        <NoImage className={'rounded-full'} width={32} height={32} />
      )}
    </div>
  );
};

const renderProductImage = (
  hit: HitProps['hit'],
  selectedSwatch: Swatch | null,
  show: boolean | undefined,
  productUrl: string,
) => {
  if (selectedSwatch && selectedSwatch.mainImageURL) {
    return (
      <Link href={getUpdatedProductUrl(productUrl)}>
        <ContentStackImage src={selectedSwatch.mainImageURL} imageHeight={220} imageWidth={220} alt="" />
      </Link>
    );
  } else if (selectedSwatch && !selectedSwatch.mainImageURL) {
    return (
      <Link href={getUpdatedProductUrl(productUrl)}>
        <NoImage className={show ? 'h-full w-[220px]' : ''} />
      </Link>
    );
  } else if (hit && hit.mainImageURL) {
    return (
      <Link href={getUpdatedProductUrl(productUrl)}>
        <ContentStackImage src={hit.mainImageURL} imageHeight={220} imageWidth={220} alt="" />
      </Link>
    );
  } else {
    return (
      <Link href={getUpdatedProductUrl(productUrl)}>
        <NoImage className={show ? 'h-full w-[220px]' : ''} />
      </Link>
    );
  }
};
